import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import SettingsModal from "./SettingsModal";
// NOTE: this file lives at src/components/ai/AiSidebar.js (same folder as
// AiSearchPage.js), so this path matches the one that worked there.
// Verify against your actual layout if it errors.
import { sessionStorageService, AUTH_ROUTES } from "../../features/authentication";
import "../../styles/ai/ai-sidebar.css";

function getDateLabel(ts) {
    if (!ts) return "Earlier";
    const date  = new Date(ts);
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const d     = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diff  = Math.floor((today - d) / 86400000);

    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";

    return date.toLocaleDateString("en-GB", {
        day:   "2-digit",
        month: "2-digit",
        year:  "numeric",
    });
}

export default function AiSidebar({
                                      chats = [],
                                      activeChatId = null,
                                      setActiveChatId = () => {},
                                      createNewChat = () => {},
                                      onRenameChat = () => {},
                                      collapsed = false,
                                      user = null,
                                      theme = "light",
                                      onToggleTheme = () => {},
                                  }) {
    const [search, setSearch] = useState("");
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [editingChatId, setEditingChatId] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const profileMenuRef = useRef(null);
    const editInputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (editingChatId) editInputRef.current?.focus();
    }, [editingChatId]);

    const startRename = (chat) => {
        setEditingChatId(chat.id);
        setEditingValue(chat.title?.trim() || "Untitled Chat");
    };

    const commitRename = () => {
        const trimmed = editingValue.trim();
        if (trimmed) onRenameChat(editingChatId, trimmed);
        setEditingChatId(null);
    };

    const cancelRename = () => setEditingChatId(null);

    const filteredChats = useMemo(() => {
        const term = search.toLowerCase().trim();
        if (!term) return chats;
        return chats.filter((c) => c.title?.toLowerCase().includes(term));
    }, [chats, search]);

    const groupedChats = useMemo(() => {
        const groups = {};
        filteredChats.forEach((chat) => {
            const label = getDateLabel(chat.createdAt);
            if (!groups[label]) groups[label] = [];
            groups[label].push(chat);
        });
        return Object.entries(groups);
    }, [filteredChats]);

    // Close the profile dropdown on outside click/tap.
    useEffect(() => {
        if (!profileMenuOpen) return undefined;

        function handlePointerDown(event) {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setProfileMenuOpen(false);
            }
        }

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
        };
    }, [profileMenuOpen]);

    const handleAccountSettings = () => {
        setProfileMenuOpen(false);
        setSettingsOpen(true);
    };

    const handleHelpSupport = () => {
        setProfileMenuOpen(false);
        window.open("mailto:info@oneglobalmarketplace.com?subject=OGM AI Support", "_blank");
    };

    const handleLogout = () => {
        sessionStorageService.remove();
        setProfileMenuOpen(false);
        navigate(AUTH_ROUTES.LOGIN, { replace: true });
    };

    if (collapsed) return null;

    return (
        <aside className="ai-sidebar">
            <div className="sidebar-header">
                <div className="sidebar-brand">
                    <div className="sidebar-brand-icon">
                        <Icon name="sparkle" size={16} />
                    </div>
                    <span className="sidebar-brand-text">OGM AI</span>
                </div>

                <button className="sidebar-new-chat" onClick={createNewChat}>
                    <Icon name="plus" size={15} />
                    New Chat
                </button>

                <div className="sidebar-search">
                    <Icon name="search" size={14} className="sidebar-search-icon" />
                    <input
                        type="text"
                        placeholder="Search chats…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="sidebar-chat-list">
                {filteredChats.length === 0 && (
                    <div className="sidebar-empty">
                        {search ? "No matches found" : "No conversations yet"}
                    </div>
                )}

                {groupedChats.map(([label, groupChats]) => (
                    <div key={label} className="sidebar-date-group">
                        <div className="sidebar-section-label">{label}</div>

                        {groupChats.map((chat) => {
                            const isActive = chat.id === activeChatId;
                            const title    = chat.title?.trim() || "Untitled Chat";
                            const isEditing = chat.id === editingChatId;

                            return (
                                <div
                                    key={chat.id}
                                    className={`sidebar-chat-item ${isActive ? "active" : ""}`}
                                    onClick={() => !isEditing && setActiveChatId(chat.id)}
                                >
                                    <Icon name="chat" size={14} className="sidebar-chat-icon" />

                                    {isEditing ? (
                                        <input
                                            ref={editInputRef}
                                            className="sidebar-chat-title-input"
                                            value={editingValue}
                                            onChange={(e) => setEditingValue(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") commitRename();
                                                if (e.key === "Escape") cancelRename();
                                            }}
                                            onBlur={commitRename}
                                        />
                                    ) : (
                                        <span className="sidebar-chat-title">{title}</span>
                                    )}

                                    {!isEditing && (
                                        <div className="sidebar-chat-actions">
                                            <button
                                                type="button"
                                                className="sidebar-action-btn"
                                                title="Rename chat"
                                                aria-label="Rename chat"
                                                onClick={(e) => { e.stopPropagation(); startRename(chat); }}
                                            >
                                                <Icon name="edit" size={13} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            <div className="sidebar-footer" ref={profileMenuRef}>
                <button
                    type="button"
                    className="sidebar-footer-trigger"
                    onClick={() => setProfileMenuOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={profileMenuOpen}
                    aria-label="Account menu"
                >
                    {user?.avatar ? (
                        <img
                            src={user.avatar}
                            alt={user.name}
                            className="sidebar-avatar sidebar-avatar-img"
                            referrerPolicy="no-referrer"
                        />
                    ) : (
                        <div className="sidebar-avatar">{user?.initials || "U"}</div>
                    )}
                    <div className="sidebar-user-info">
                        <div className="sidebar-user-name" title={user?.name || "User"}>
                            {user?.name || "User"}
                        </div>
                        <div className="sidebar-user-role">{user?.role || "Member"}</div>
                    </div>
                    <Icon
                        name="chevronDown"
                        size={14}
                        className={`sidebar-footer-chevron ${profileMenuOpen ? "open" : ""}`}
                    />
                </button>

                {profileMenuOpen && (
                    <div className="sidebar-profile-menu" role="menu">
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-profile-menu-item"
                            onClick={handleAccountSettings}
                        >
                            <Icon name="settings" size={15} />
                            Account Settings
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-profile-menu-item"
                            onClick={handleHelpSupport}
                        >
                            <Icon name="chat" size={15} />
                            Help &amp; Support
                        </button>
                        <div className="sidebar-profile-menu-divider" />
                        <button
                            type="button"
                            role="menuitem"
                            className="sidebar-profile-menu-item danger"
                            onClick={handleLogout}
                        >
                            <Icon name="logout" size={15} />
                            Logout
                        </button>
                    </div>
                )}
            </div>

            <SettingsModal
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                user={user}
                theme={theme}
                onToggleTheme={onToggleTheme}
            />
        </aside>
    );
}